import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatPage.css';
import './AgentChatPage.css';
import api from '../api';
import { useGenies } from './GenieContext';
import TableView from './TableView';
import { useConversations } from '../hooks/useConversations';

/**
 * Parse a markdown string into an array of segments:
 *   { type: 'text', content: string }
 *   { type: 'table', headers: string[], data: object[] }
 *
 * A "markdown table block" is a contiguous run of lines where every line
 * starts with '|'.  The first such line is the header row, the second is
 * the separator (|---|...|), and the rest are data rows.
 */
function parseMarkdownToSegments(text) {
  if (!text) return [];

  const lines = text.split('\n');
  const segments = [];
  let i = 0;
  let textBuffer = [];

  const flushText = () => {
    const content = textBuffer.join('\n').trim();
    if (content) segments.push({ type: 'text', content });
    textBuffer = [];
  };

  while (i < lines.length) {
    const line = lines[i];

    // Detect start of a markdown table: line starts with '|' and next non-separator line also starts with '|'
    if (line.trimStart().startsWith('|')) {
      // Collect the full table block (all consecutive lines starting with '|')
      const tableLines = [];
      while (i < lines.length && lines[i].trimStart().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }

      // Need at least header + separator + 1 data row
      if (tableLines.length >= 3) {
        // Parse header row
        const headerRow = tableLines[0];
        const headers = headerRow
          .split('|')
          .map(h => h.trim())
          .filter(h => h.length > 0);

        // tableLines[1] is the separator row (|---|---|) — skip it
        // Parse data rows
        const dataRows = tableLines.slice(2).map(rowLine => {
          const cells = rowLine
            .split('|')
            .map(c => c.trim())
            .filter((_, idx, arr) => (idx > 0 && idx < arr.length - 1) || arr.length === 1);
          // Build row object keyed by header
          const row = {};
          headers.forEach((h, hIdx) => {
            row[h] = cells[hIdx] !== undefined ? cells[hIdx] : '';
          });
          return row;
        });

        if (headers.length > 0 && dataRows.length > 0) {
          flushText();
          segments.push({ type: 'table', headers, data: dataRows });
          continue;
        }
      }

      // Fallback: treat as text
      textBuffer.push(...tableLines);
      continue;
    }

    textBuffer.push(line);
    i++;
  }

  flushText();
  return segments;
}

/**
 * AgentChatPage — chat interface for Databricks MAS (Multi-Agent Supervisor) endpoints.
 *
 * Route: /agent/:agentId
 *
 * Uses the same sidebar + main-chat-area layout as GenericChatPage so the
 * side menu is visible and functional, including last-10 conversation history.
 */
function AgentChatPage() {
  const { agentId: agentIdParam } = useParams();
  const navigate = useNavigate();
  const context = useGenies();
  const contextGenies = context?.genies;
  const selectedClient = context?.selectedClient || null;
  const isGenieListLoading = context?.isLoading || false;

  // Resolve the agent from context
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    if (isGenieListLoading) return;
    if (!agentIdParam) return;
    const genies = contextGenies || [];
    const found = genies.find(g => g.type === 'agent' && g.id === agentIdParam);
    if (found) {
      setAgent(found);
    }
  }, [agentIdParam, contextGenies, isGenieListLoading]);

  // Chat state
  const [messages, setMessages] = useState([]); // { role: 'user'|'assistant', content: string }
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Sidebar state (mirrors GenericChatPage)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Conversation history state
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  const [deletingConversationId, setDeletingConversationId] = useState(null);

  // Stable ref for messages so sendMessage can read the current list without
  // adding `messages` to its useCallback deps (avoids stale closures).
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; });

  // Conversation hook
  const {
    conversations,
    loadConversations,
    loadConversation,
    createConversation,
    updateConversationActivity,
    saveAgentMessages,
    deleteConversation,
  } = useConversations();

  // Load conversation history whenever agent or client changes
  useEffect(() => {
    if (agent?.id) {
      loadConversations(agent.id, selectedClient || null);
    }
  }, [agent?.id, selectedClient, loadConversations]);

  // Reset conversation state when client switches
  const prevClientRef = useRef(selectedClient);
  useEffect(() => {
    if (prevClientRef.current !== selectedClient) {
      prevClientRef.current = selectedClient;
      setMessages([]);
      setActiveConversationId(null);
      console.log('[AgentChatPage] Client changed — resetting conversation state');
    }
  }, [selectedClient]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input on mount / when agent resolves
  useEffect(() => {
    inputRef.current?.focus();
  }, [agent]);

  // ── Format relative time (same logic as GenericChatPage) ───────────────────
  const formatTime = (isoString) => {
    const now = new Date();
    const messageTime = new Date(isoString);
    const diffInSeconds = Math.max(0, Math.floor((now - messageTime) / 1000));
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return diffInHours === 1 ? '1h ago' : `${diffInHours} hrs ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  };

  // ── Load a historical conversation into the chat window ───────────────────
  const loadHistoryConversation = useCallback(async (conversationId) => {
    try {
      setLoadingConversationId(conversationId);
      const data = await loadConversation(conversationId);

      if (data && Array.isArray(data.messages) && data.messages.length > 0) {
        setActiveConversationId(conversationId);

        // Transform stored messages back to component format.
        // Agent messages are always text (no charts/tables), so this is
        // simpler than the Genie equivalent.
        const transformed = data.messages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(msg => ({ role: msg.role, content: msg.content || '' }));

        setMessages(transformed);
      } else {
        console.warn('[AgentChatPage] Conversation returned no messages.');
        setMessages([{
          role: 'assistant',
          content: '⚠️ **This conversation could not be loaded.**\n\nThe messages may no longer be available. Please start a new chat.',
        }]);
      }
    } catch (err) {
      console.error('[AgentChatPage] Error loading conversation:', err);
      setMessages([{
        role: 'assistant',
        content: '⚠️ **Failed to load conversation.**\n\nAn error occurred while retrieving this chat. Please try again or start a new chat.',
      }]);
    } finally {
      setLoadingConversationId(null);
    }
  }, [loadConversation]);

  // ── Delete a conversation from the sidebar ────────────────────────────────
  const handleDeleteConversation = useCallback(async (e, conversationId) => {
    e.stopPropagation();
    if (deletingConversationId) return;
    setDeletingConversationId(conversationId);
    try {
      const ok = await deleteConversation(conversationId);
      if (ok && conversationId === activeConversationId) {
        setMessages([]);
        setActiveConversationId(null);
      }
    } finally {
      setDeletingConversationId(null);
    }
  }, [deletingConversationId, deleteConversation, activeConversationId]);

  // ── Send a message ────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (question) => {
    if (!question.trim() || isLoading || !agent) return;

    const userMsg = { role: 'user', content: question.trim() };
    const currentMessages = messagesRef.current;
    const updatedHistory = [...currentMessages, userMsg];
    setMessages(updatedHistory);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Build conversation history for context (exclude the current question — it's sent separately)
      const historyForApi = currentMessages.map(m => ({ role: m.role, content: m.content }));

      const response = await api.post('/api/agent/ask', {
        agentId: agent.id,
        endpointName: agent.endpointName,
        question: question.trim(),
        conversationHistory: historyForApi,
        clientName: selectedClient,
      }, {
        // MAS endpoints can take 30–60 s — override axios default timeout
        timeout: 90000,
      });

      const assistantMsg = { role: 'assistant', content: response.data.answer };
      const finalMessages = [...updatedHistory, assistantMsg];
      setMessages(finalMessages);

      // ── Persist conversation to DB ────────────────────────────────────────
      // On the FIRST message of a new conversation, create a DB record then
      // immediately save the full message history.
      // On subsequent messages, save updated messages + bump updated_at.
      const currentActiveId = activeConversationId;
      if (currentMessages.length === 0 && !currentActiveId) {
        // First message — create a new conversation record
        const newConv = await createConversation(
          agent.id,                          // genieId (reused field — agent id)
          agent.name,                        // genieName (agent display name)
          question.trim().substring(0, 100), // title = first 100 chars of question
          null,                              // databricksConversationId (not applicable)
          agent.id,                          // databricksSpaceId (reused as agent id)
          selectedClient || null,            // client isolation key
          null                               // spnName (not applicable for agents)
        );
        if (newConv) {
          setActiveConversationId(newConv.id);
          // Persist the first exchange immediately after creating the record
          saveAgentMessages(newConv.id, finalMessages);
        }
      } else if (currentActiveId) {
        // Subsequent message — save full updated history + bump last-activity
        saveAgentMessages(currentActiveId, finalMessages);
        updateConversationActivity(currentActiveId);
      }
    } catch (err) {
      const errMsg = err.response?.data?.details || err.response?.data?.error || err.message || 'An error occurred';
      setError(errMsg);
      console.error('[AgentChatPage] Error:', errMsg);
    } finally {
      setIsLoading(false);
      // Re-focus input after response
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [agent, isLoading, selectedClient, activeConversationId, createConversation, updateConversationActivity, saveAgentMessages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
    setActiveConversationId(null);
    inputRef.current?.focus();
  };

  // ── Suggested prompts per agent (matched by name substring) ─────────────
  const AGENT_SUGGESTED_PROMPTS = {
    'parts inventory': [
      'Which parts are at risk of running out soon, and which locations need replenishment?',
      'What parts are used most frequently, and how should we adjust reorder points or stocking levels?',
      'What parts do we need for upcoming PMs?',
      'What is the current inventory count for each Storeroom?',
    ],
    'lifecycle cost': [
      'Which assets have the highest total lifecycle cost, and what are the primary cost drivers?',
      'When should we replace an asset to minimize lifecycle cost while maintaining reliable service?',
      'How do lifecycle costs compare across asset types, manufacturers, models, or operating locations?',
    ],
    'default': [
      'Which assets have the most maintenance issues this year?',
      'What parts are running low and need reordering?',
      'Show me comeback repair trends for the last quarter.',
    ],
  };

  const getSuggestedPrompts = (agentName) => {
    if (!agentName) return AGENT_SUGGESTED_PROMPTS['default'];
    const lower = agentName.toLowerCase();
    if (lower.includes('lifecycle cost')) return AGENT_SUGGESTED_PROMPTS['lifecycle cost'];
    if (lower.includes('parts inventory')) return AGENT_SUGGESTED_PROMPTS['parts inventory'];
    return AGENT_SUGGESTED_PROMPTS['default'];
  };

  const SUGGESTED_PROMPTS = getSuggestedPrompts(agent?.name);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="chat-page-container">

      {/* Loading overlay when fetching a historical conversation */}
      {loadingConversationId && (
        <div className="conversation-loading-overlay">
          <div className="conversation-loading-content">
            <div className="conversation-loading-spinner-large"></div>
            <div className="conversation-loading-text">Loading conversation...</div>
          </div>
        </div>
      )}

      {/* ── Sidebar (matches GenericChatPage layout) ── */}
      <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <button
            onClick={() => navigate('/genies')}
            className="back-button"
            aria-label="Back to AI Studio"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          {!isSidebarCollapsed && <div className="sidebar-header-spacer" />}
          <button
            className="sidebar-toggle"
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            aria-label="Toggle sidebar"
          >
            <svg width="20" height="20" viewBox="0 0 20 20">
              <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
              <rect x="4" y="4" width="6" height="2" rx="1" fill="currentColor"/>
              <rect x="4" y="7" width="6" height="2" rx="1" fill="currentColor"/>
              <rect x="4" y="10" width="6" height="2" rx="1" fill="currentColor"/>
            </svg>
          </button>
        </div>

        {/* Collapsed state: just show new-chat button */}
        {isSidebarCollapsed && (
          <button
            type="button"
            className="new-chat-button-collapsed"
            onClick={handleClearChat}
            aria-label="New Chat"
            title="New Chat"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>
        )}

        {/* Expanded sidebar content */}
        {!isSidebarCollapsed && (
          <>
            <div className="sidebar-title">
              <h3 className="sidebar-title-text">
                AI Studio
              </h3>
            </div>

            <div className="sidebar-actions">
              {/* Search bar */}
              <div className="search-bar chat-sidebar-search">
                <svg className="search-bar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="search-bar-input"
                  aria-label="Search conversations"
                />
              </div>

              <button
                type="button"
                className="new-chat-button"
                onClick={handleClearChat}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span>New Chat</span>
              </button>
            </div>

            {/* Recent chat history */}
            <div className="recent-history">
              <h4 className="recent-history-title">Recent Chats</h4>
              <ul className="recent-history-list">
                {conversations
                  .filter(conv => conv.title.toLowerCase().includes(searchQuery.toLowerCase()))
                  .slice(0, 10)
                  .map(conv => {
                    const displayTitle = conv.title.length > 28 ? conv.title.slice(0, 28) + '…' : conv.title;
                    const isConvLoading = loadingConversationId === conv.id;
                    const isDeleting = deletingConversationId === conv.id;
                    return (
                      <li
                        key={conv.id}
                        onClick={() => !isConvLoading && !isDeleting && loadHistoryConversation(conv.id)}
                        className={`recent-chat-item${isConvLoading ? ' loading' : ''}`}
                      >
                        <div className="history-item-text-container">
                          <img src="/icon-chat.svg" alt="" className="history-chat-icon" width="15" height="13" />
                          <span className="history-item-title" title={conv.title}>{displayTitle}</span>
                          {isConvLoading && <span className="history-loading-spinner" />}
                        </div>
                        <div className="history-item-actions">
                          <span className="time">{formatTime(conv.updated_at)}</span>
                          <button
                            type="button"
                            className="delete-conversation-btn"
                            aria-label="Delete conversation"
                            title="Delete conversation"
                            onClick={e => handleDeleteConversation(e, conv.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <span className="history-loading-spinner" style={{ marginLeft: 0 }} />
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6"/>
                                <path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
              {conversations.filter(conv => conv.title.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10).length === 0 && (
                <p className="recent-history-empty">
                  {conversations.length === 0 ? 'No recent chats yet' : 'No matches for search'}
                </p>
              )}
            </div>

            {/* Footer */}
            <div
              className="sidebar-footer-tools"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/BG-Chat.png)` }}
            >
              <p className="sidebar-footer-tools-title">Explore individual Genie spaces</p>
              <Link to="/genies" className="sidebar-learn-more-link">AI Studio</Link>
            </div>
          </>
        )}
      </div>

      {/* ── Main chat area ── */}
      <div className={`main-chat-area ${isSidebarCollapsed ? 'expanded' : ''}`}>

        {/* Loading state while genies are being fetched */}
        {isGenieListLoading ? (
          <div className="welcome-screen">
            <div className="agent-loading-spinner" style={{ margin: '80px auto' }} />
            <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading agent...</p>
          </div>
        ) : !agent ? (
          /* Agent not found */
          <div className="welcome-screen">
            <h2 style={{ color: '#fff' }}>Agent Not Found</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>
              The agent you're looking for is not available for your account.
            </p>
            <button className="agent-back-btn" onClick={() => navigate('/genies')} style={{ marginTop: 24 }}>
              ← Back to AI Studio
            </button>
          </div>
        ) : messages.length === 0 && !isLoading ? (
          /* Welcome / empty state */
          <div className="welcome-screen">
            <div className="welcome-top-row">
              <div className="welcome-message-block">
                <div className="agent-badge-header" style={{ marginBottom: 8 }}>
                  <span className="agent-badge-icon">⚡</span>
                  <span className="agent-badge-label">Multi-Agent AI</span>
                </div>
                <h1 className="welcome-title">{agent.name}</h1>
                <p className="welcome-name" style={{ fontSize: 14, opacity: 0.7, fontWeight: 400 }}>
                  Coordinates 5 specialized analysts to answer your fleet questions.
                </p>
              </div>
            </div>

            <div className="initial-questions-section">
              <div className="predefined-questions">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    type="button"
                    className="predefined-question"
                    onClick={() => sendMessage(prompt)}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Chat messages */
          <div className="chat-window">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <>
                    {parseMarkdownToSegments(msg.content).map((seg, segIdx) =>
                      seg.type === 'table' ? (
                        <TableView key={segIdx} headers={seg.headers} data={seg.data} />
                      ) : (
                        <ReactMarkdown key={segIdx} remarkPlugins={[remarkGfm]}>{seg.content}</ReactMarkdown>
                      )
                    )}
                  </>
                ) : (
                  <p>{msg.content}</p>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="message assistant">
                <div className="agent-typing-indicator">
                  <span className="agent-typing-dot" />
                  <span className="agent-typing-dot" />
                  <span className="agent-typing-dot" />
                  <span className="agent-loading-label">Agent is analyzing your question…</span>
                </div>
              </div>
            )}

            {error && (
              <div className="agent-error-banner">
                <span>⚠️ {error}</span>
                <button onClick={() => setError(null)}>✕</button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        )}

        {/* Input area */}
        <div className="chat-input-wrapper">
          <div className="chat-input">
            <textarea
              ref={inputRef}
              className="chat-input-field agent-textarea"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={!agent ? 'Loading agent…' : 'Ask the agent a question… (Enter to send, Shift+Enter for new line)'}
              disabled={isLoading || !agent}
              rows={1}
              aria-label="Ask your question"
            />
            <button
              type="button"
              className="chat-input-send"
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading || !agent}
              aria-label="Send"
            >
              {isLoading ? (
                <span className="agent-send-spinner" />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m22 2-7 20-4-9-9-4Z"/>
                  <path d="M22 2 11 13"/>
                </svg>
              )}
            </button>
          </div>
          <p className="disclaimer">AI agents may make mistakes. Verify critical information independently.</p>
        </div>
      </div>
    </div>
  );
}

export default AgentChatPage;
